import type { Href } from "expo-router";
import { useRouter } from "expo-router";

import { ProgramCard, type ProgramCardPhase } from "../aspects/ProgramCard";
import { PANE_THEMES } from "../brand/paneThemes";
import {
  caseCategoryOverline,
  daysRemainingUntilClose,
} from "../../lib/studentCases";
import { formatDateShort } from "../../lib/datetime";
import type { CompetitionStatus, StudentCaseRow } from "../../types/database";

const STUDENT_THEME = PANE_THEMES.Student;

export type DashboardProgramCardAccent = "active" | "judging";
export type DashboardProgramCardTheme = "light" | "dark";

const TOTAL_PHASES = 5;

function phaseFromStatus(status: CompetitionStatus): ProgramCardPhase {
  switch (status) {
    case "judging":
      return { index: 4, total: TOTAL_PHASES, name: "Final review" };
    case "closed":
    case "final_invoice_pending":
    case "final_invoice_paid":
      return { index: TOTAL_PHASES, total: TOTAL_PHASES, name: "Results & wrap-up" };
    case "open":
    default:
      return { index: 1, total: TOTAL_PHASES, name: "Discovery & framing" };
  }
}

/**
 * "My programs" card on the student Home view. Thin wrapper around
 * the shared ProgramCard aspect — pre-computes data from a
 * StudentCaseRow and hands it to the aspect.
 */
export function DashboardProgramCard({
  row,
  accent = "active",
  theme = "light",
}: {
  row: StudentCaseRow;
  accent?: DashboardProgramCardAccent;
  theme?: DashboardProgramCardTheme;
}) {
  const router = useRouter();
  const onPress = () => {
    router.push(`/student/competition/${row.id}` as Href);
  };

  const daysLeft = daysRemainingUntilClose(row.closes_at);
  const closesDateShort = row.closes_at ? formatDateShort(row.closes_at) : null;
  const sponsorName =
    typeof row.company_name === "string" ? row.company_name.trim() : "";

  return (
    <ProgramCard
      theme={STUDENT_THEME}
      status={accent}
      surface={theme}
      typeMeta={caseCategoryOverline(row)}
      title={row.name}
      sponsorName={sponsorName.length > 0 ? sponsorName : undefined}
      phase={phaseFromStatus(row.status)}
      daysLeft={daysLeft}
      closesDateShort={closesDateShort}
      onPress={onPress}
      accessibilityLabel={`Open program: ${row.name}`}
    />
  );
}
